// root '../../../';

// dependencies
import { Child } from 'pym.js';
import { select, selectAll, event as d3Event } from 'd3-selection';
import { format } from 'd3-format';

// polyfills
// import fetch from 'unfetch';
// import './utils/polyfills'; (includes Array.findIndex && Array.find)

// utils & data
import data from './data/data.json';

// styles
import './styles/graphic.scss';


// Store current filter (will be set to first category on initial load)
let currentFilter = null;

window.pymChild = new Child();

function draw() {
  const timeline = select('#graphic').html('');

  // Get unique categories from all entries (flatten arrays)
  const allCategories = new Set();
  data.forEach((d) => {
    if (d.categories && Array.isArray(d.categories)) {
      d.categories.forEach(cat => allCategories.add(cat));
    }
  });
  const categories = [...Array.from(allCategories).sort(), 'all'];

  // Set default filter to Media on initial load
  if (currentFilter === null) {
    currentFilter = 'Media';
  }

  // Add dynamic headline based on current filter
  const headlineText = currentFilter === 'all'
    ? 'All examples'
    : `Examples involving ${currentFilter.toLowerCase()}`;

  timeline.append('h2')
    .attr('class', 'filter-headline')
    .text(headlineText);

  // Add filter buttons
  const filterContainer = timeline.append('div')
    .attr('class', 'filter-container');

  filterContainer.append('span')
    .attr('class', 'filter-label')
    .text('Click to explore more topics: ');

  const filters = filterContainer.selectAll('.filter-btn')
    .data(categories)
    .enter()
    .append('button')
    .attr('class', d => `filter-btn ${d === currentFilter ? 'active' : ''}`)
    .attr('data-category', d => d)
    .text(d => (d === 'all' ? 'View all' : d))
    .on('click', function () {
      currentFilter = this.getAttribute('data-category');
      draw();
      window.pymChild.sendHeight();
      // Re-trigger animation check after redraw
      if (typeof checkAndAnimateEntries === 'function') {
        checkAndAnimateEntries();
      }
    });

  // Render timeline entries
  let section;
  let figure;
  let currentHeader = null;

  data.forEach((row, idx) => {
    if (row.type == 'header') {
      // Store header for later - only add if next entry matches filter
      currentHeader = row;
      return;
    }

    // Skip if filtering and doesn't match
    // Check if any of the entry's categories match the current filter
    const matchesFilter = currentFilter === 'all'
      || (row.categories && row.categories.includes(currentFilter));

    if (row.type === 'text' && !matchesFilter) {
      currentHeader = null; // Clear stored header since this entry doesn't match
      return;
    }

    // Add the header if we have one stored
    if (currentHeader) {
      // Use first category for data-category attribute (for styling)
      const primaryCategory = (row.categories && row.categories.length > 0) ? row.categories[0] : '';

      section = timeline.append('div')
        .attr('class', 'timeline-entry')
        .attr('data-category', primaryCategory);

      // Add header with category tags inline
      const headerContainer = section.append('div')
        .attr('class', 'timeline-header-container');

      headerContainer.append('h3')
        .attr('class', 'timeline-header')
        .html(currentHeader.content);

      // Add category tags next to the header if present (show all categories)
      if (row.categories && row.categories.length > 0) {
        row.categories.forEach((category) => {
          headerContainer.append('span')
            .attr('class', 'category-tag')
            .attr('data-category', category)
            .text(category);
        });
      }

      currentHeader = null;
    }

    if (row.type == 'text' && section) {
      const textP = section.append('p')
        .attr('class', 'timeline-copy')
        .html(row.content);
    }

    if (row.type == 'photo' && section) {
      figure = section.append('figure');
      figure.append('img')
        .attr('src', `media/${row.content}`);
    }

    if (row.type == 'caption' && figure) {
      figure.append('figcaption')
        .html(row.content);
    }

    if (row.type == 'html' && section) {
      section.append('div')
        .html(row.content);
    }
  });
}

// Pym scroll-based animation for timeline entries
// This tracks the iframe's position in the PARENT page viewport,
// so scroll animations work correctly when embedded in articles

let isEmbedded = false; // Track if we're in an embedded context

function checkVisibleEntries(positionString) {
  // Parse the position string: "scrollTop viewportHeight iframeTop iframeBottom"
  const positions = positionString.split(' ').map(parseFloat);
  const scrollTop = positions[0];
  const viewportHeight = positions[1];
  const iframeTop = positions[2];
  const iframeBottom = positions[3];

  const entries = document.querySelectorAll('.timeline-entry');

  entries.forEach((entry) => {
    const rect = entry.getBoundingClientRect();
    // Element's position relative to the parent page
    const entryTop = rect.top + iframeTop;

    // Calculate visibility ratio (similar to the example code)
    // Positive values mean element is in or approaching viewport
    const distanceFromViewportTop = entryTop - scrollTop;
    const visibilityRatio = 1 - (distanceFromViewportTop / viewportHeight);

    // Element is "in view" if ratio > 0.1 (appears when ~10% in viewport)
    // This provides a nice buffer so animation starts before fully visible
    if (visibilityRatio > 0.1 && !entry.classList.contains('is-visible')) {
      entry.classList.add('is-visible');
    }
  });
}

// IntersectionObserver for standalone viewing
let observer = null;

function setupStandaloneScrollAnimation() {
  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
      }
    });
  }, {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px',
  });

  // Observe all timeline entries
  document.querySelectorAll('.timeline-entry').forEach((entry) => {
    observer.observe(entry);
  });
}

// Function to check entries after filter changes
function checkAndAnimateEntries() {
  if (!isEmbedded) {
    // Not embedded - re-setup IntersectionObserver for new entries
    if (observer) {
      observer.disconnect();
    }
    setupStandaloneScrollAnimation();
  } else {
    // If embedded, request current position from parent to trigger check
    window.pymChild.sendMessage('request-viewport-iframe-position');
  }
}

// Set up Pym scroll listener after pymChild is initialized
window.onload = () => {
  const { pymChild } = window;
  draw(); // Initial render
  pymChild.sendHeight();

  // Listen for scroll position updates from parent page
  pymChild.onMessage('viewport-iframe-position', (position) => {
    isEmbedded = true;
    checkVisibleEntries(position);
  });

  // Fallback for standalone viewing (not in iframe)
  // After a short delay, check if we're getting Pym messages
  setTimeout(() => {
    if (!isEmbedded) {
      // Not embedded - use IntersectionObserver for scroll animation
      setupStandaloneScrollAnimation();
    }
  }, 100);
};
